import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
;import './CertificateCriteriaForm.scss'
import AppContainer from 'app_components/layouts/app';
import BreadcrumbComponent from 'app_components/commons/breadcrumb';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ICourseSchedulingModeListItem } from 'app_types/admin/courseScheduling/courseSchedulingModeTypes';
import CourseSchedulingModeService from 'app_services/admin/courseScheduling/courseSchedulingModeService';
import ReactSelectComponent from 'app_components/commons/reactSelect';
import { useForm } from 'react-hook-form';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import { MdOutlineCloudUpload } from 'react-icons/md';
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent';
import CheckBoxComponent from 'app_components/commons/checkBox/containers/checkBoxComponent';
import { routingService } from 'app_services/routingService';
import { CertificateCriteriaTypeCourse, ICertificateCriteria } from 'app_types/admin/certificateCriteria/certificateCriteriaTypes';
import ModalAttached from 'app_components/modules/ModalAttached/ModalAttached';
import { IAttached } from 'app_types/admin/attached/attached.types';
import CertificateCriteriaService from 'app_services/admin/certificateCriteria/CertificateCriteriaService';
import { ScreenNotificationsUtil } from 'app_utils/screenNotification';
import AttachedService from 'app_services/admin/attached/attachedService';
import { CourseSchedulingModes } from 'app_types/admin/courseScheduling/courseSchedulingModeTypes';

const CertificateCriteriaForm: FC = () => {
  const { certificateCriteriaId } = useParams<{ certificateCriteriaId: string }>()
  const [schedulingModes, setSchedulingModes] = useState<ICourseSchedulingModeListItem[]>([])
  const [openCertificateCriteria, setOpenCertificateCriteria] = useState<boolean>(false)
  const [attachSaved, setAttachSaved] = useState<IAttached | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [certificateCriteria, setCertificateCriteria] = useState<ICertificateCriteria>()
  const certificateCriteriaService = useMemo(() => new CertificateCriteriaService(), [])
  const screenNotification = useMemo(() => new ScreenNotificationsUtil(), [])
  const attachedService = useMemo(() => new AttachedService(), [])
  const formHook = useForm<ICertificateCriteria>()
  const currentCourseType = formHook.watch('typeCourse')
  const isNew = certificateCriteriaId === 'new'
  const history = useHistory()

  useEffect(() => {
    formHook.register('typeCourse')
    formHook.register('certificateCriteria', { required: true })
  }, [])

  const getCertificateCriteriaById = useCallback(async () => {
    const response = await certificateCriteriaService.getById({ id: certificateCriteriaId })
    if (response.code === 200) {
      setCertificateCriteria(response.register)
    }
  }, [certificateCriteriaService, certificateCriteriaId])

  useEffect(() => {
    if (certificateCriteriaId && !isNew) {
      getCertificateCriteriaById()
    }
  }, [isNew, certificateCriteriaId, getCertificateCriteriaById])

  const getAttachedById = useCallback(async () => {
    const response = await attachedService.getById({ _id: certificateCriteria?.certificateCriteria as string })
    if (response?.code === 200) {
      setAttachSaved(response.attached)
    }
  }, [attachedService, certificateCriteria?._id])

  useEffect(() => {
    if (certificateCriteria?._id) {
      // @ts-ignore
      formHook.setValue('modality', { label: certificateCriteria.modality.name, value: certificateCriteria.modality._id })
      getAttachedById()
      if (certificateCriteria?.typeCourse) {
        formHook.setValue('typeCourse', certificateCriteria.typeCourse)
      }
    }
  }, [certificateCriteria?._id])

  const courseSchedulingModeService = useMemo(() => new CourseSchedulingModeService(), [])

  const getSchedulingModes = useCallback(async () => {
    const response = await courseSchedulingModeService.list({moodle_id: true})
    if (response?.courseSchedulingModes) {
      setSchedulingModes(response.courseSchedulingModes)
    }
  }, [courseSchedulingModeService])

  useEffect(() => {
    getSchedulingModes()
  }, [getSchedulingModes])

  const schedulingModesOptions = useMemo(() => schedulingModes.map((mode) => ({ label: mode.name, value: mode._id })), [schedulingModes])

  const handleChangeType = useCallback((newType: CertificateCriteriaTypeCourse) => {
    if (newType === currentCourseType) {
      formHook.setValue('typeCourse', null)
    } else {
      formHook.setValue('typeCourse', newType)
    }
  }, [currentCourseType, formHook])

  useEffect(() => {
    if (attachSaved?._id) {
      formHook.setValue('certificateCriteria', attachSaved._id)
    }
  }, [attachSaved?._id, formHook])

  const handleSubmit = async (data: ICertificateCriteria) => {
    // @ts-ignore
    data.modality = data?.modality?.value
    try {
      setIsLoading(true)
      if (isNew) {
        const response = await certificateCriteriaService.create(data)
        if (response.code === 200) {
          await screenNotification.showSuccessTimer()
          history.push(routingService.getSecurePath('/admin/certificate-criteria'))
        }
      } else {
        const response = await certificateCriteriaService.update({ id: certificateCriteriaId, ...data })
        if (response.code === 200) {
          await screenNotification.showSuccessTimer()
          history.push(routingService.getSecurePath('/admin/certificate-criteria'))
        }
      }
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      console.log(e)
    }
  }

  // @ts-ignore
  const isVirtual = formHook.watch('modality')?.label === CourseSchedulingModes.VIRTUAL

  useEffect(() => {
    if (!isVirtual) {
      formHook.setValue('typeCourse', null)
    }
  }, [isVirtual])

  return (
    <AppContainer>
      <div className="certificate-criteria-form--layout">
        <BreadcrumbComponent
          links={[
            '/admin/certificate-criteria',
            '#',
          ]}
          texts={[
            'Criterios de certificación',
            isNew ? 'Crear nuevo criterio' : 'Editar criterio'
          ]}
        />
        <div>
          <h3>{isNew ? 'Crear nuevo criterio de certificación' : 'Editar criterio de certificación'}</h3>
        </div>
        <form onSubmit={formHook.handleSubmit(handleSubmit)} className="certificate-criteria-form--form">
          <div className="form-group">
            <LabelComponent>Modalidad</LabelComponent>
            <ReactSelectComponent
              identifier='modality'
              options={schedulingModesOptions}
              formHooks={formHook}
              isMulti={false}
              required={true}
            />
            {/* @ts-ignore */}
            {formHook.errors.schedulingMode && <div className='custom-invalid-feedback'>Este campo es requerido</div>}
          </div>
          {isVirtual && (
            <div className='certificate-criteria--form-check-container'>
              <LabelComponent>Tipo de curso</LabelComponent>
              <div className='certificate-criteria--form-check-item'>
                <CheckBoxComponent checked={currentCourseType === CertificateCriteriaTypeCourse.FREE} onClick={() => handleChangeType(CertificateCriteriaTypeCourse.FREE)} />
                <LabelComponent>Gratuito</LabelComponent>
              </div>
              <div className='certificate-criteria--form-check-item'>
                <CheckBoxComponent checked={currentCourseType === CertificateCriteriaTypeCourse.MOOC} onClick={() => handleChangeType(CertificateCriteriaTypeCourse.MOOC)} />
                <LabelComponent>Mooc</LabelComponent>
              </div>
            </div>
          )}
          <ButtonComponent width='240px' variant='primary' type='button' onClick={() => setOpenCertificateCriteria(true)}>
            <ButtonComponent.Icon>
              <MdOutlineCloudUpload />
            </ButtonComponent.Icon>
            Agregar criterio
          </ButtonComponent>
          {attachSaved?.files?.[0]?.url && (
            <Link to={{ pathname: attachSaved.files[0].url }} target='_blank'>{attachSaved.files[0].url}</Link>
          )}
          <div className="certificate-criteria-form--container-actions">
            <Link to={routingService.getSecurePath('/admin/certificate-criteria')}>
              <ButtonComponent>
                Cancelar
              </ButtonComponent>
            </Link>
            <ButtonComponent loading={isLoading} variant='primary' type='submit'>
              Guardar
            </ButtonComponent>
          </div>
        </form>
        <ModalAttached
          open={openCertificateCriteria}
          onClose={() => setOpenCertificateCriteria(false)}
          title="Criterios de certificación"
          // @ts-ignore
          formHook={formHook}
          required
          name='certificateCriteria'
          category='course_scheduling_certificate_criteria'
          onFileSaved={setAttachSaved}
          defaultValue={attachSaved?._id}
      />
      </div>
    </AppContainer>
  )
}

export default CertificateCriteriaForm
